import React, { useState, useEffect } from "react";
import wordList from "/src/assets/wordchain-duel/words.json";

const SAMPLE_WORDS = wordList;

const difficultyMap = {
  easy: 0.2,
  medium: 0.5,
  hard: 1,
};

function WordChainDuel() {
  const [mode, setMode] = useState(null);
  const [subMode, setSubMode] = useState(null);
  const [chain, setChain] = useState([]);
  const [input, setInput] = useState("");
  const [turn, setTurn] = useState("player1");
  const [usedWords, setUsedWords] = useState(new Set());
  const [error, setError] = useState("");
  const [gameOver, setGameOver] = useState(false);
  const [player1Name, setPlayer1Name] = useState("");
  const [player2Name, setPlayer2Name] = useState("");
  const [showNameInputs, setShowNameInputs] = useState(false);

  const wordList = SAMPLE_WORDS;

  function validateWord(word) {
    if (!wordList.includes(word.toLowerCase())) return "Word not in dictionary.";
    if (usedWords.has(word.toLowerCase())) return "Word already used.";
    if (chain.length > 0) {
      const lastChar = chain[chain.length - 1].slice(-1);
      if (word[0].toLowerCase() !== lastChar.toLowerCase()) {
        return `Word must start with '${lastChar}'.`;
      }
    }
    return "";
  }

  function submitWord() {
    const word = input.trim().toLowerCase();
    const validationError = validateWord(word);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    setChain((prev) => [...prev, word]);
    setUsedWords((prev) => new Set(prev).add(word));
    setInput("");

    if (mode === "ai") setTurn("ai");
    else if (mode === "vs") setTurn(turn === "player1" ? "player2" : "player1");
  }

  useEffect(() => {
    if (mode !== "ai" || turn !== "ai" || gameOver) return;

    const lastWord = chain[chain.length - 1];
    const lastChar = lastWord.slice(-1).toLowerCase();

    const candidates = wordList.filter(
      (w) => w[0].toLowerCase() === lastChar && !usedWords.has(w)
    );

    if (candidates.length === 0) {
      setGameOver(true);
      setError("AI cannot find a valid word. You win! 🎉");
      return;
    }

    const difficulty = difficultyMap[subMode] || 0.5;
    const sorted = [...candidates].sort();
    const index = Math.floor(difficulty * (sorted.length - 1));
    const aiWord = sorted[index];

    setTimeout(() => {
      setChain((prev) => [...prev, aiWord]);
      setUsedWords((prev) => new Set(prev).add(aiWord));
      setTurn("player1");
    }, 1000);
  }, [turn, mode, subMode, chain, usedWords, gameOver]);

  function startGame(selectedMode, selectedSubMode) {
    setSubMode(selectedSubMode);
    if (selectedMode === "ai") setPlayer2Name("AI");
    setChain([]);
    setUsedWords(new Set());
    setTurn("player1");
    setError("");
    setInput("");
    setGameOver(false);
  }

  return (
    <div className="max-w-xl mx-auto p-6 bg-[#0d0d1a] text-white rounded-xl shadow-2xl border border-cyan-500">
      <h1 className="text-2xl text-center font-bold text-cyan-400 mb-4">Word Chain Duel</h1>

      {!mode ? (
        <div className="space-y-6 text-center">
          <p className="text-lg">Choose Game Mode</p>
          <button onClick={() => { setMode("ai"); setShowNameInputs(true); }} className="neon-btn">
            Play vs AI
          </button>
          <button onClick={() => { setMode("vs"); setShowNameInputs(true); }} className="neon-btn">
            Player vs Player
          </button>
        </div>
      ) : !subMode ? (
        <div className="space-y-4 text-center">
          <p className="text-lg">Choose Difficulty / Match Type</p>

          {showNameInputs && (
            <div className="space-y-3 text-left">
              <label className="block">
                <span>Player 1 Name</span>
                <input
                  value={player1Name}
                  onChange={(e) => setPlayer1Name(e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded bg-[#1a1a2e] border border-cyan-500 text-white"
                />
              </label>
              {mode === "vs" && (
                <label className="block">
                  <span>Player 2 Name</span>
                  <input
                    value={player2Name}
                    onChange={(e) => setPlayer2Name(e.target.value)}
                    className="mt-1 w-full px-3 py-2 rounded bg-[#1a1a2e] border border-cyan-500 text-white"
                  />
                </label>
              )}
            </div>
          )}

          {mode === "ai" ? (
            <>
              <button onClick={() => {
                if (!player1Name) return setError("Please enter Player 1 name.");
                startGame("ai", "easy");
              }} className="neon-btn">Easy</button>
              <button onClick={() => {
                if (!player1Name) return setError("Please enter Player 1 name.");
                startGame("ai", "medium");
              }} className="neon-btn">Medium</button>
              <button onClick={() => {
                if (!player1Name) return setError("Please enter Player 1 name.");
                startGame("ai", "hard");
              }} className="neon-btn">Hard</button>
            </>
          ) : (
            <>
              <button onClick={() => {
                if (!player1Name || !player2Name) return setError("Enter both player names.");
                startGame("vs", "same");
              }} className="neon-btn">Same Device</button>
              <button  className="neon-btn">
                Online Match (Coming Soon)
              </button>
            </>
          )}

          {error && <p className="text-red-500 text-sm">{error}</p>}
          <button onClick={() => setMode(null)} className="text-sm underline text-cyan-300 hover:text-white transition">
            ← Back
          </button>
        </div>
      ) : (
        <>
          <div className="mb-3 text-sm text-gray-400">
            <div>
              Mode: <span className="text-white font-medium">{mode === "ai" ? `AI (${subMode})` : `PvP (${subMode})`}</span>
            </div>
            <div>
              {gameOver
                ? "Game Over"
                : turn === "ai"
                  ? "🤖 AI is thinking..."
                  : mode === "ai"
                    ? `🧠 ${player1Name}'s turn`
                    : `${turn === "player1" ? `🟢 ${player1Name}` : `🟡 ${player2Name}`}'s turn`}
            </div>
          </div>

          <div className="mb-4 p-3 rounded bg-black border border-cyan-700 overflow-y-auto max-h-60 space-y-2 scroll-box">
            {chain.length === 0 ? (
              <p className="text-gray-400">Start with any word</p>
            ) : (
              chain.map((word, i) => {
                const isP1 = i % 2 === 0;
                const name = isP1 ? player1Name : player2Name;
                return (
                  <div key={i} className={`flex ${isP1 ? "justify-start" : "justify-end"}`}>
                    <div className={`rounded-lg px-4 py-2 text-sm font-mono max-w-xs break-words ${isP1
                      ? "bg-gradient-to-r from-green-600 to-green-800 text-white"
                      : "bg-gradient-to-l from-pink-600 to-pink-800 text-white"
                      }`}>
                      <strong>{name}:</strong> {word}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {!gameOver && turn !== "ai" && (
            <div className="space-y-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitWord()}
                placeholder={
                  chain.length === 0
                    ? "Enter starting word"
                    : `Start with '${chain[chain.length - 1]?.slice(-1)}'`
                }
                className="w-full px-4 py-2 rounded bg-[#1a1a2e] border border-cyan-500 text-white focus:outline-none focus:ring focus:ring-cyan-400"
              />
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <button onClick={submitWord} className="neon-btn" disabled={input.trim() === ""}>
                Submit Word
              </button>
            </div>
          )}

          <button
            onClick={() => {
              setMode(null);
              setSubMode(null);
              setShowNameInputs(false);
              setPlayer1Name("");
              setPlayer2Name("");
            }}
            className="mt-6 neon-btn bg-gray-700 hover:bg-gray-600"
          >
            Restart / Change Mode
          </button>
        </>
      )}
    </div>
  );
}

export default WordChainDuel;
